import { Resource } from '@modelcontextprotocol/sdk/types.js';
import type { Config } from '../types/index.js';
import { findSmartStackStructure, findEntityFiles } from '../lib/detector.js';
import { findFiles, readText } from '../utils/fs.js';
import path from 'path';

export const dbSchemaResourceTemplate: Resource = {
  uri: 'smartstack://schema/',
  name: 'SmartStack Database Schema',
  description: 'Database schema information. Use smartstack://schema/{table} to get specific table.',
  mimeType: 'text/markdown',
};

interface EntityInfo {
  name: string;
  tableName: string;
  properties: PropertyInfo[];
  relationships: RelationshipInfo[];
  file: string;
}

interface PropertyInfo {
  name: string;
  type: string;
  nullable: boolean;
  isPrimaryKey: boolean;
  maxLength?: number;
}

interface RelationshipInfo {
  type: 'one-to-one' | 'one-to-many' | 'many-to-many';
  targetEntity: string;
  propertyName: string;
}

export async function getDbSchemaResource(
  config: Config,
  tableFilter?: string
): Promise<string> {
  const structure = await findSmartStackStructure(config.smartstack.projectPath);

  if (!structure.domain && !structure.infrastructure) {
    return '# Database Schema\n\nNo domain or infrastructure project found.';
  }

  const entities: EntityInfo[] = [];

  // Parse entities from domain layer
  if (structure.domain) {
    const entityFiles = await findEntityFiles(structure.domain);
    for (const file of entityFiles) {
      const entity = await parseEntity(file, structure.root, config);
      if (entity) {
        entities.push(entity);
      }
    }
  }

  // Parse configurations from infrastructure layer for additional info
  if (structure.infrastructure) {
    await enrichFromConfigurations(entities, structure.infrastructure, config);
  }

  // Filter if specified
  const filteredEntities = tableFilter
    ? entities.filter(e =>
      e.name.toLowerCase().includes(tableFilter.toLowerCase()) ||
      e.tableName.toLowerCase().includes(tableFilter.toLowerCase())
    )
    : entities;

  return formatSchema(filteredEntities, tableFilter, config);
}

async function parseEntity(
  filePath: string,
  rootPath: string,
  _config: Config
): Promise<EntityInfo | null> {
  const content = await readText(filePath);

  // Find class declaration
  const classMatch = content.match(/public\s+(?:class|record)\s+(\w+)(?:\s*:\s*(\w+))?/);
  if (!classMatch) return null;

  const entityName = classMatch[1];

  // Skip non-entity classes
  if (entityName.endsWith('Dto') || entityName.endsWith('Command') ||
    entityName.endsWith('Query') || entityName.endsWith('Handler')) {
    return null;
  }

  const properties: PropertyInfo[] = [];
  const relationships: RelationshipInfo[] = [];

  // Parse properties
  const propertyPattern = /public\s+(?:required\s+)?(\w+(?:<[\w,\s]+>)?)\??\s+(\w+)\s*\{/g;
  let match;

  while ((match = propertyPattern.exec(content)) !== null) {
    const propertyType = match[1];
    const propertyName = match[2];

    // Skip navigation properties for now (we'll add them as relationships)
    if (propertyType.startsWith('ICollection') || propertyType.startsWith('List')) {
      // This is a collection navigation property
      const targetMatch = propertyType.match(/<(\w+)>/);
      if (targetMatch) {
        relationships.push({
          type: 'one-to-many',
          targetEntity: targetMatch[1],
          propertyName,
        });
      }
      continue;
    }

    // Check if it's a reference navigation property
    const isNavigationProperty = /^[A-Z]/.test(propertyType) &&
      !['Guid', 'String', 'Int32', 'Int64', 'DateTime', 'DateTimeOffset',
        'Boolean', 'Decimal', 'Double', 'Float', 'Byte'].includes(propertyType) &&
      !propertyType.includes('?');

    if (isNavigationProperty && !propertyType.includes('<')) {
      relationships.push({
        type: 'one-to-one',
        targetEntity: propertyType,
        propertyName,
      });
      continue;
    }

    properties.push({
      name: propertyName,
      type: mapCSharpType(propertyType),
      nullable: content.includes(`${propertyType}? ${propertyName}`) ||
        content.includes(`${propertyType}?  ${propertyName}`),
      isPrimaryKey: propertyName === 'Id' || propertyName === `${entityName}Id`,
    });
  }

  // Default table name based on conventions (using cfg_ as default prefix for generic configs)
  const tableName = `cfg_${entityName}s`;

  return {
    name: entityName,
    tableName,
    properties,
    relationships,
    file: path.relative(rootPath, filePath),
  };
}

async function enrichFromConfigurations(
  entities: EntityInfo[],
  infrastructurePath: string,
  _config: Config
): Promise<void> {
  const configFiles = await findFiles('**/Configurations/**/*.cs', {
    cwd: infrastructurePath,
  });

  for (const file of configFiles) {
    const content = await readText(file);

    // Find ToTable calls
    const tableMatch = content.match(/\.ToTable\s*\(\s*"([^"]+)"/);
    if (tableMatch) {
      // Find which entity this configuration is for
      const entityMatch = content.match(/IEntityTypeConfiguration<(\w+)>/);
      if (entityMatch) {
        const entityName = entityMatch[1];
        const entity = entities.find(e => e.name === entityName);
        if (entity) {
          entity.tableName = tableMatch[1];
        }
      }
    }

    // Find MaxLength constraints
    const maxLengthMatches = content.matchAll(/\.Property\s*\(\s*\w+\s*=>\s*\w+\.(\w+)\s*\)[\s\S]*?\.HasMaxLength\s*\(\s*(\d+)\s*\)/g);
    for (const match of maxLengthMatches) {
      const propertyName = match[1];
      const maxLength = parseInt(match[2], 10);

      for (const entity of entities) {
        const property = entity.properties.find(p => p.name === propertyName);
        if (property) {
          property.maxLength = maxLength;
        }
      }
    }
  }
}

function mapCSharpType(csharpType: string): string {
  const typeMap: Record<string, string> = {
    'Guid': 'uniqueidentifier',
    'string': 'nvarchar',
    'String': 'nvarchar',
    'int': 'int',
    'Int32': 'int',
    'long': 'bigint',
    'Int64': 'bigint',
    'DateTime': 'datetime2',
    'DateTimeOffset': 'datetimeoffset',
    'bool': 'bit',
    'Boolean': 'bit',
    'decimal': 'decimal',
    'Decimal': 'decimal',
    'double': 'float',
    'Double': 'float',
    'float': 'real',
    'Float': 'real',
    'byte[]': 'varbinary',
  };

  // Handle nullable types
  const baseType = csharpType.replace('?', '');
  return typeMap[baseType] || 'nvarchar';
}

function formatSchema(
  entities: EntityInfo[],
  filter?: string,
  _config?: Config
): string {
  const lines: string[] = [];

  lines.push('# SmartStack Database Schema');
  lines.push('');

  if (filter) {
    lines.push(`> Filtered by: \`${filter}\``);
    lines.push('');
  }

  if (entities.length === 0) {
    lines.push('No entities found matching the criteria.');
    return lines.join('\n');
  }

  // Summary
  lines.push('## Summary');
  lines.push('');
  lines.push(`**Total Entities**: ${entities.length}`);
  lines.push('');

  // Table list
  lines.push('| Entity | Table | Properties | Relationships |');
  lines.push('|--------|-------|------------|---------------|');
  for (const entity of entities) {
    lines.push(
      `| ${entity.name} | \`${entity.tableName}\` | ${entity.properties.length} | ${entity.relationships.length} |`
    );
  }
  lines.push('');

  // Detailed schema for each entity
  for (const entity of entities) {
    lines.push(`## ${entity.name}`);
    lines.push('');
    lines.push(`**Table**: \`${entity.tableName}\``);
    lines.push(`**File**: \`${entity.file}\``);
    lines.push('');

    // Properties
    lines.push('### Columns');
    lines.push('');
    lines.push('| Column | SQL Type | Nullable | Notes |');
    lines.push('|--------|----------|----------|-------|');

    for (const prop of entity.properties) {
      const notes: string[] = [];
      if (prop.isPrimaryKey) notes.push('PK');
      if (prop.maxLength) notes.push(`MaxLength(${prop.maxLength})`);

      lines.push(
        `| ${prop.name} | ${prop.type} | ${prop.nullable ? 'Yes' : 'No'} | ${notes.join(', ')} |`
      );
    }
    lines.push('');

    // Relationships
    if (entity.relationships.length > 0) {
      lines.push('### Relationships');
      lines.push('');
      lines.push('| Type | Target | Property |');
      lines.push('|------|--------|----------|');

      for (const rel of entity.relationships) {
        const typeIcon = rel.type === 'one-to-one' ? '1:1' :
          rel.type === 'one-to-many' ? '1:N' : 'N:N';
        lines.push(`| ${typeIcon} | ${rel.targetEntity} | ${rel.propertyName} |`);
      }
      lines.push('');
    }

    // Sample EF configuration
    lines.push('### EF Core Configuration');
    lines.push('');
    lines.push('```csharp');
    lines.push(`public class ${entity.name}Configuration : IEntityTypeConfiguration<${entity.name}>`);
    lines.push('{');
    lines.push(`    public void Configure(EntityTypeBuilder<${entity.name}> builder)`);
    lines.push('    {');
    lines.push(`        builder.ToTable("${entity.tableName}");`);
    lines.push('');
    const pk = entity.properties.find(p => p.isPrimaryKey);
    if (pk) {
      lines.push(`        builder.HasKey(e => e.${pk.name});`);
    }
    lines.push('    }');
    lines.push('}');
    lines.push('```');
    lines.push('');
  }

  // ER Diagram (text-based)
  if (entities.length > 1) {
    lines.push('## Entity Relationships');
    lines.push('');
    lines.push('```');

    for (const entity of entities) {
      for (const rel of entity.relationships) {
        const arrow = rel.type === 'one-to-one' ? '──' :
          rel.type === 'one-to-many' ? '─<' : '>─<';
        lines.push(`${entity.name} ${arrow} ${rel.targetEntity}`);
      }
    }

    lines.push('```');
    lines.push('');
  }

  return lines.join('\n');
}
